import * as React from 'react';
import { AlertTriangle, Check, Sparkles, Users } from 'lucide-react';
import {
  Callout,
  FilterChip,
  PartLabel,
  WorkspaceButton,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import {
  declineEvidence,
  saveRoom,
  type DeclineReason,
  type HeaderAttendee,
  type RoomEntry,
  type Speaker,
  type TranscriptDetail,
} from '@/lib/api/discovery-review';
import {
  resolveDuplicate,
  useDiscoveryWrite,
  usePeopleQuery,
  type InterviewDetail,
} from '@/lib/api/discovery-sessions';
import { cn } from '@/lib/utils/cn';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { errorText } from '../sessions/errors';
import { firstName } from '../sessions/sessions-logic';
import {
  attendeeName,
  pickOf,
  roomIntro,
  showsInRoom,
  suggestionWhy,
  waitsOnRoom,
  type RoomPick,
} from './review-logic';

/**
 * "Who was in the room?" (design note section 3): the first step after a
 * session. Speaker 1..N are matched to people in one save, with the
 * transcript header's attendee list offered first, the interviewer marked
 * (never a source), and the same-person question answered in place before
 * saving (rule 6). A match the AI reader made arrives pre-filled on the AI
 * tint and is only a suggestion: saving the room confirms it, which is what
 * releases its items for review. A speaker cited by confirmed evidence is
 * fixed (rule 8). Code decides nothing here that a person has not seen.
 */

interface Pick {
  value: RoomPick;
  /** The same-person answer for a new attendee who may be someone in People. */
  same?: string | 'different';
}

const DECLINE_SPEAKER: ReadonlyArray<[DeclineReason, string]> = [
  ['duplicate', 'Duplicate'],
  ['wrong_person', 'Wrong person'],
  ['small_talk', 'Small talk'],
];

/** Everyone the room names once it is saved: "Maria Lopez · Speaker 1 (interviewer)". */
export function roomSummary(t: TranscriptDetail): string {
  const names: string[] = [];
  for (const sp of t.speakers) {
    if (sp.is_interviewer) names.push(`${sp.label} (interviewer)`);
    else if (sp.person && !names.includes(sp.person.full_name)) names.push(sp.person.full_name);
  }
  return names.join(' · ');
}

/** The room is done when no speaker with items waits on it and no same-person flag is open. */
export function roomDone(t: TranscriptDetail): boolean {
  return !t.speakers.some(
    (sp) => waitsOnRoom(sp) || (sp.possible_duplicate && sp.proposal_count > 0),
  );
}

export function RoomStep({
  engagementId,
  d,
  t,
  onSaved,
}: {
  engagementId: string;
  d: InterviewDetail;
  t: TranscriptDetail;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const people = usePeopleQuery(engagementId);
  const attendees = (t.header_attendees ?? []).filter((a) => !a.avero);
  const avero = (t.header_attendees ?? []).filter((a) => a.avero);
  const initial = React.useMemo(
    () => Object.fromEntries(t.speakers.map((sp) => [sp.id, { value: pickOf(sp) } as Pick])),
    [t.speakers],
  );
  const [picks, setPicks] = React.useState<Record<string, Pick>>(initial);
  React.useEffect(() => setPicks(initial), [initial]);
  const [showQuiet, setShowQuiet] = React.useState(false);
  const [declining, setDeclining] = React.useState<string | null>(null);

  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });
  const save = useDiscoveryWrite(engagementId, (entries: RoomEntry[]) =>
    saveRoom(engagementId, d.interview.id, t.transcript.transcript_id, entries),
  );
  const resolve = useDiscoveryWrite(
    engagementId,
    (v: { personId: string; decision: 'merge' | 'different' }) =>
      resolveDuplicate(engagementId, v.personId, v.decision),
  );
  const decline = useDiscoveryWrite(
    engagementId,
    async (v: { ids: string[]; code: DeclineReason }) => {
      for (const id of v.ids) await declineEvidence(engagementId, id, { reason_code: v.code });
      return v.ids.length;
    },
  );

  const active = (people.data?.items ?? []).filter(
    (p) => p.status !== 'retired' && !p.possible_duplicate_of,
  );
  const listed = new Set(attendees.map((a) => a.person?.id).filter(Boolean));
  // Speaker 1, 2 … 10 in order (names alphabetically).
  const ordered = [...t.speakers].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  const visible = ordered.filter(showsInRoom);
  const quiet = ordered.filter((sp) => !showsInRoom(sp));
  const rows = showQuiet ? ordered : visible;
  const waiting = t.speakers.filter(waitsOnRoom).reduce((n, sp) => n + sp.proposal_count, 0);
  const used = new Set(Object.values(picks).map((p) => p.value));

  /** The body to save for a pick, or null when it names nobody or waits on a question. */
  const entryOf = (sp: Speaker, p: Pick): RoomEntry | null => {
    if (p.value === '@interviewer') return { speaker_id: sp.id, is_interviewer: true };
    if (p.value.startsWith('p:')) return { speaker_id: sp.id, person_id: p.value.slice(2) };
    if (p.value.startsWith('new:')) {
      const a = attendees[Number(p.value.slice(4))];
      if (!a?.name) return null;
      if (a.candidates.length > 0) {
        if (!p.same) return null;
        if (p.same !== 'different') return { speaker_id: sp.id, person_id: p.same };
        return {
          speaker_id: sp.id,
          new_person: {
            full_name: a.name,
            ...(a.email ? { email: a.email } : {}),
            match_decision: { decision: 'different', candidate_ids: a.candidates.map((c) => c.id) },
          },
        };
      }
      return {
        speaker_id: sp.id,
        new_person: { full_name: a.name, ...(a.email ? { email: a.email } : {}) },
      };
    }
    return null;
  };
  const asked = t.speakers.filter((sp) => {
    const p = picks[sp.id];
    if (!p?.value.startsWith('new:')) return false;
    const a = attendees[Number(p.value.slice(4))];
    return !!a && a.candidates.length > 0 && !p.same;
  });
  // What the save writes: a change, or an AI suggestion confirmed as it stands.
  const entries = t.speakers
    .filter((sp) => !sp.frozen)
    .flatMap((sp) => {
      const p = picks[sp.id] ?? { value: '' };
      const changed = p.value !== initial[sp.id]?.value || p.same !== undefined;
      if (!changed && !sp.suggested) return [];
      const e = entryOf(sp, p);
      return e ? [e] : [];
    });
  const unmatched = visible.filter(
    (sp) => sp.proposal_count > 0 && !(picks[sp.id]?.value ?? '') && !sp.frozen,
  );
  // The items the save lets through to review: those of speakers it names as a person.
  const releases = entries
    .filter((e) => !('is_interviewer' in e))
    .reduce((n, e) => n + (t.speakers.find((s) => s.id === e.speaker_id)?.proposal_count ?? 0), 0);

  const onSave = () =>
    save.mutate(entries, {
      onSuccess: () => {
        toast({
          message:
            releases > 0
              ? `Saved who was in the room. ${plural(releases, 'item is', 'items are')} ready to review; the confident ones are selected.`
              : 'Saved who was in the room.',
        });
        onSaved?.();
      },
      onError: fail,
    });

  const hint = asked.length
    ? 'Answer the same-person question first.'
    : unmatched.length
      ? `${unmatched.map((s) => s.label).join(', ')} ${unmatched.length === 1 ? 'is' : 'are'} not matched: ${unmatched.length === 1 ? 'its' : 'their'} items keep waiting.`
      : 'A match is fixed once evidence from that speaker is confirmed.';

  return (
    <div>
      <p className="mb-3 text-meta text-ink-500">{roomIntro(t.speakers, waiting)}</p>
      {(t.header_attendees ?? []).length > 0 ? (
        <>
          <PartLabel>Attendees in the transcript header</PartLabel>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {[...attendees, ...avero].map((a, i) => {
              const ticked =
                (a.person && used.has(`p:${a.person.id}`)) ||
                used.has(`new:${attendees.indexOf(a)}`) ||
                (a.avero && used.has('@interviewer'));
              return (
                <span
                  key={`${attendeeName(a)}-${i}`}
                  className={cn(
                    'inline-flex h-[26px] items-center gap-1.5 rounded-full border pl-1 pr-2.5 text-meta font-medium',
                    ticked
                      ? 'border-posture-healthy-tint bg-posture-healthy-tint text-posture-healthy'
                      : 'border-ink-100 bg-paper-0 text-ink-700',
                  )}
                >
                  <PersonAvatar
                    id={a.person?.id ?? attendeeName(a)}
                    name={attendeeName(a)}
                    size={20}
                  />
                  {attendeeName(a)}
                  {a.avero ? <span className="font-normal text-ink-500">· Avero</span> : null}
                  {ticked ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
                </span>
              );
            })}
          </div>
        </>
      ) : null}

      {rows.map((sp) => {
        const p = picks[sp.id] ?? { value: '' };
        const newAttendee = p.value.startsWith('new:') ? attendees[Number(p.value.slice(4))] : null;
        const why = p.value === initial[sp.id]?.value ? suggestionWhy(sp) : null;
        const flagged = sp.person && sp.possible_duplicate && !sp.is_interviewer;
        const options = active.filter((x) => !listed.has(x.id));
        if (sp.person && !active.some((x) => x.id === sp.person!.id) && !listed.has(sp.person.id)) {
          options.push({ ...sp.person, status: 'active' } as (typeof active)[number]);
        }
        const theirs = t.proposals.filter((x) => x.speaker_id === sp.id).map((x) => x.evidence_id);
        return (
          <div
            key={sp.id}
            className="grid grid-cols-[80px_minmax(0,1fr)] items-start gap-x-3.5 gap-y-1.5 border-b border-ink-100 py-3 last:border-b-0 xl:grid-cols-[92px_minmax(0,1fr)_250px]"
          >
            <div className="font-mono text-meta font-semibold leading-[30px] text-ink-900">
              {sp.label}
            </div>
            <div className="text-body-sm text-ink-700">
              {sp.sample_line ? `“${sp.sample_line.text}”` : null}
              <span className="mt-0.5 block text-meta text-ink-500">
                {plural(sp.line_count ?? 0, 'line')} ·{' '}
                {sp.proposal_count ? `${plural(sp.proposal_count, 'item')} to review` : 'no items'}
              </span>
            </div>
            <div className="col-start-2 flex flex-col gap-1 xl:col-start-auto">
              <WorkspaceSelect
                aria-label={`Who ${sp.label} is`}
                disabled={sp.frozen || save.isPending}
                className={cn(
                  'h-[30px] py-0 text-meta',
                  sp.suggested && p.value === initial[sp.id]?.value
                    ? 'border-dashed border-ai-surface-border bg-ai-surface text-ai-ink'
                    : !p.value && 'text-ink-500',
                )}
                value={p.value}
                onChange={(e) =>
                  setPicks((cur) => ({ ...cur, [sp.id]: { value: e.target.value } }))
                }
              >
                {p.value === '' ? (
                  <option value="" disabled>
                    Choose who this is
                  </option>
                ) : null}
                {attendees.length > 0 ? (
                  <optgroup label="Attendees in the file">
                    {attendees.map((a, i) =>
                      a.name || a.person ? (
                        <option key={i} value={a.person ? `p:${a.person.id}` : `new:${i}`}>
                          {a.person ? a.person.full_name : `${a.name} (new)`}
                          {a.person?.title ? `, ${a.person.title}` : ''}
                        </option>
                      ) : null,
                    )}
                  </optgroup>
                ) : null}
                <optgroup label="Avero (the interviewer, never a source)">
                  <option value="@interviewer">
                    {avero.length > 0
                      ? `The interviewer: ${avero.map(attendeeName).join(' or ')}`
                      : 'The interviewer'}
                  </option>
                </optgroup>
                <optgroup label="Someone else in People">
                  {options.map((x) => (
                    <option key={x.id} value={`p:${x.id}`}>
                      {x.full_name}
                      {x.title ? `, ${x.title}` : ''}
                    </option>
                  ))}
                </optgroup>
              </WorkspaceSelect>
              {sp.frozen ? (
                <span className="text-[11px] leading-4 text-ink-500">
                  Fixed: confirmed evidence comes from this speaker.
                </span>
              ) : why ? (
                <span className="flex gap-1 text-[11px] leading-4 text-ai-ink">
                  <Sparkles className="mt-0.5 h-3 w-3 flex-none" aria-hidden="true" />
                  <span>{why}</span>
                </span>
              ) : null}
              {!p.value && theirs.length > 0 && !sp.frozen ? (
                declining === sp.id ? (
                  <span
                    role="group"
                    aria-label={`Decline the items from ${sp.label}`}
                    className="flex flex-wrap items-center gap-1"
                  >
                    {DECLINE_SPEAKER.map(([code, label]) => (
                      <FilterChip
                        key={code}
                        disabled={decline.isPending}
                        onClick={() =>
                          decline.mutate(
                            { ids: theirs, code },
                            {
                              onSuccess: (n) => {
                                setDeclining(null);
                                toast({
                                  message: `Declined ${plural(n, 'item')} from ${sp.label} (${label.toLowerCase()}). They stay on record and never count.`,
                                });
                              },
                              onError: fail,
                            },
                          )
                        }
                      >
                        {label}
                      </FilterChip>
                    ))}
                    <button
                      type="button"
                      className={linkishClass()}
                      onClick={() => setDeclining(null)}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={cn(linkishClass(true), 'self-start')}
                    onClick={() => setDeclining(sp.id)}
                  >
                    Not sure who? Decline its {plural(theirs.length, 'item')}
                  </button>
                )
              ) : null}
            </div>
            {newAttendee && newAttendee.candidates.length > 0 ? (
              <div className="col-start-2 xl:col-span-2">
                {p.same ? (
                  <div className="text-meta text-ink-500">
                    {p.same === 'different'
                      ? `A different person: added to People as ${newAttendee.name}.`
                      : `The same person as ${newAttendee.candidates.find((c) => c.id === p.same)?.full_name}: one source.`}{' '}
                    <button
                      type="button"
                      className={linkishClass()}
                      onClick={() => setPicks((cur) => ({ ...cur, [sp.id]: { value: p.value } }))}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <Callout
                    tone="ask"
                    icon={Users}
                    actions={
                      <>
                        {newAttendee.candidates.map((c) => (
                          <WorkspaceButton
                            key={c.id}
                            onClick={() =>
                              setPicks((cur) => ({
                                ...cur,
                                [sp.id]: { value: p.value, same: c.id },
                              }))
                            }
                          >
                            {newAttendee.candidates.length === 1
                              ? 'Same person'
                              : `Same as ${firstName(c.full_name)}`}
                          </WorkspaceButton>
                        ))}
                        <WorkspaceButton
                          variant="tertiary"
                          onClick={() =>
                            setPicks((cur) => ({
                              ...cur,
                              [sp.id]: { value: p.value, same: 'different' },
                            }))
                          }
                        >
                          Different person
                        </WorkspaceButton>
                      </>
                    }
                  >
                    Is <b>{newAttendee.name}</b> the same person as{' '}
                    {newAttendee.candidates.map((c, i) => (
                      <React.Fragment key={c.id}>
                        {i > 0 ? ' or ' : ''}
                        <b>{c.full_name}</b>
                        {c.title ? `, ${c.title}` : ''}
                      </React.Fragment>
                    ))}
                    , already in People?
                  </Callout>
                )}
              </div>
            ) : null}
            {flagged ? (
              <div className="col-start-2 xl:col-span-2">
                <DuplicateQuestion
                  sp={sp}
                  twin={
                    people.data?.items.find((x) => x.id === sp.person!.id)?.possible_duplicate_of ??
                    null
                  }
                  busy={resolve.isPending}
                  onResolve={(decision) =>
                    resolve.mutate(
                      { personId: sp.person!.id, decision },
                      {
                        onSuccess: () =>
                          toast({
                            message:
                              decision === 'merge'
                                ? `Merged “${sp.person!.full_name}”. Everything they say counts as one source.`
                                : `Recorded: “${sp.person!.full_name}” is a different person.`,
                          }),
                        onError: fail,
                      },
                    )
                  }
                />
              </div>
            ) : null}
          </div>
        );
      })}

      {quiet.length > 0 && !showQuiet ? (
        <div className="mt-2 text-meta text-ink-500">
          {plural(quiet.length, 'more speaker')} with no items (
          {quiet.map((q) => `${q.label}, ${plural(q.line_count ?? 0, 'line')}`).join('; ')}):
          nothing waits on {quiet.length === 1 ? 'it' : 'them'}.{' '}
          <button type="button" className={linkishClass()} onClick={() => setShowQuiet(true)}>
            Show
          </button>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 text-meta text-ink-500">{hint}</span>
        <WorkspaceButton
          variant="primary"
          size="md"
          disabled={entries.length === 0 || asked.length > 0 || save.isPending}
          onClick={onSave}
        >
          {save.isPending
            ? 'Saving…'
            : releases > 0
              ? `Save and review ${plural(releases, 'item')}`
              : 'Save'}
        </WorkspaceButton>
      </div>
    </div>
  );
}

/** A mapped person flagged as a possible duplicate: Merge or Different person, in place. */
function DuplicateQuestion({
  sp,
  twin,
  busy,
  onResolve,
}: {
  sp: Speaker;
  twin: { full_name: string } | null;
  busy: boolean;
  onResolve: (decision: 'merge' | 'different') => void;
}) {
  return (
    <Callout
      tone="warn"
      icon={AlertTriangle}
      actions={
        <>
          <WorkspaceButton disabled={busy} onClick={() => onResolve('merge')}>
            Same person
          </WorkspaceButton>
          <WorkspaceButton
            variant="tertiary"
            disabled={busy}
            onClick={() => onResolve('different')}
          >
            Different person
          </WorkspaceButton>
        </>
      }
    >
      Is <b>{sp.person!.full_name}</b>{' '}
      {twin ? (
        <>
          the same person as <b>{twin.full_name}</b>,{' '}
        </>
      ) : (
        ''
      )}
      already in People? Their items wait until you say.
    </Callout>
  );
}
